import React, { useContext, useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  SvgIcon,
  Select,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Stack,
  Box,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GetStockResponse } from '../../../app/services/api/generated';
import { StockFilterOptions } from './interfaces/StockFilterOptions';
import StockOnHandTable from './StockOnHandTable';
import { SearchSm } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import { Section } from '../../../components/ui/Section';
import { handleSearchFieldKeyDown } from '../../../utils';

const StockOnHandBox = (props: {
  stockOnHands: GetStockResponse,
  loadData: (options: StockFilterOptions) => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { logError } = useLogError();
  const defaultSort = 'itemNumber';
  const defaultTake = 10;
  const defaultSkip = 0;
  const defaultDirection = 'desc';

  const { user, hasPermissionTo } = useContext(UserContext);
  const [page, setPage] = useState(0);
  const [categoryFilters, setCategoryFilters] = useState('');
  const [stockFilters, setStockFilters] = useState('');
  const [storeFilters, setStoreFilters] = useState('');
  const [category, setCategory] = React.useState<string[]>([]);
  const [stores, setStores] = React.useState<string[]>([]);
  const [stock, setStock] = React.useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [lastSearch, setLastSearch] = useState('');
  const [hasRecentActivity, setHasRecentActivity] = useState(true);
  const [isMadeInStore, setIsMadeInStore] = useState<boolean>(false);
  const [isOrderableQuantity, setIsOrderableQuantity] = useState<boolean>(true);
  const hasPermissionToReadMadeInStore = hasPermissionTo(['MadeInStoreRead']);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const stockLevelOptions = [
    { id: 'positivestock', name: t('stockOnHand.positiveStock') },
    { id: 'negativestock', name: t('stockOnHand.negativeStock') },
    { id: 'belowminimumquantity', name: t('stockOnHand.belowMinimumQuantity') },
    { id: 'outofstock', name: t('stockOnHand.outOfStock') },
  ];
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearch(newSearchValue);
  };

  const handleCategoryChange = (event: SelectChangeEvent<typeof category>) => {
    const {
      target: { value },
    } = event;

    setCategory(typeof value === 'string' ? value.split(',') : value);
    setCategoryFilters(typeof value === 'string' ? value : value.join(','));
  };

  const handleStoreChange = (event: SelectChangeEvent<typeof stores>) => {
    const {
      target: { value },
    } = event;
    setStores(typeof value === 'string' ? value.split(',') : value);

    if (typeof value === 'string') {
      setStoreFilters(value);
    } else {
      const storeKeys = value
        .map((item) => user?.stores?.find((x) => x.storeName === item)?.storeNumber)
        .filter((key) => key !== undefined);

      setStoreFilters(storeKeys.join(','));
    }
  };

  const handleStockLevelChange = (event: SelectChangeEvent<typeof stock>) => {
    const {
      target: { value },
    } = event;
    setStock(typeof value === 'string' ? value.split(',') : value);

    if (typeof value === 'string') {
      setStockFilters(value);
    }
    else {
      const stockLevelIds = value
        .map((item) => stockLevelOptions.find((x) => x.name === item)?.id)
        .filter((id) => id !== undefined);
      setStockFilters(stockLevelIds.join(','));
    }
  };

  const handleRecentActivity = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasRecentActivity(event.target.checked);
  };

  const handleIsMadeInStore = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsMadeInStore(event.target.checked);
  };

  const callLoadDataFromAPI = (options: StockFilterOptions) => {
    props.loadData(options);
  };

  const loadDataFromOrderTable = (fieldToSort: string, sortDirection: string, skip: number, take: number) => {
    callLoadDataFromAPI({
      search,
      fieldToSort,
      sortDirection,
      skip,
      take,
      filters: stockFilters,
      categoryFilters,
      storeFilters,
      hasRecentActivity,
      isMadeInStore,
    });
  };

  const clearAll = () => {
    setPage(1);
    setCategoryFilters('');
    setCategory([]);
    setStock([]);
    setStockFilters('');
    setStoreFilters('');
    setStores([]);
    setHasRecentActivity(false);
    setSearch('');

    callLoadDataFromAPI({
      search: '',
      fieldToSort: defaultSort,
      sortDirection: defaultDirection,
      skip: defaultSkip,
      take: defaultTake,
      filters: undefined,
      categoryFilters: undefined,
      storeFilters: undefined,
      hasRecentActivity: undefined,
      isMadeInStore: undefined,
    });
  };

  const searchCall = () => {
    setPage(1);
    setLastSearch(search);
    callLoadDataFromAPI({
      search,
      fieldToSort: undefined,
      sortDirection: undefined,
      skip: defaultSkip,
      take: defaultTake,
      filters: stockFilters,
      categoryFilters,
      storeFilters,
      hasRecentActivity,
      isMadeInStore,
    });
  };

  return (
    <Grid container
      spacing={5}
      sx={{ pt: '6px' }}>
      <Grid item
        xs={12} >
        <Stack direction={{ sm: 'row', xs: 'column' }}
          width='100%'
          spacing={5}
          alignItems={{ xs: 'stretch', sm: 'center' }}>
          {/* Search Field */}
          <Box flexGrow={1}>
            <FormControl fullWidth>
              <TextField
                label={t('stockOnHand.itemNumberOrDescription')}
                size="small"
                id="input-with-icon-textfield"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon>
                        <SearchSm />
                      </SvgIcon>
                    </InputAdornment>
                  ),
                }}
                value={search}
                onChange={handleSearchChange}
                onKeyDown={e => handleSearchFieldKeyDown(e, searchCall)}
              />
            </FormControl>
          </Box>

          {/* Switch: Recent Activity */}
          <FormControlLabel
            control={
              <Switch checked={hasRecentActivity}
                onChange={handleRecentActivity} />
            }
            label={t('stockOnHand.recentActivity')}
            labelPlacement="start"
          />

          {/* Switch: Made in Store */}
          {hasPermissionToReadMadeInStore && (
            <FormControlLabel
              control={
                <Switch checked={isMadeInStore}
                  onChange={handleIsMadeInStore} />
              }
              label={t('stockOnHand.madeInStore')}
              labelPlacement="start"
            />
          )}
          {/* Switch: Orderable Units */}
          <FormControlLabel
            control={
              <Switch checked={isOrderableQuantity}
                onChange={(_, checked) => setIsOrderableQuantity(checked)} />
            }
            label={t('stockOnHand.orderableUnits')}
            labelPlacement="start"
          />
        </Stack>
      </Grid>
      <Grid item
        xs={12}
        md={4}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="stock-stock-level-label">{t('stockOnHand.stockLevel')}</InputLabel>
          <Select
            labelId="stock-stock-level-label"
            id="stock-stock-level"
            size='small'
            label={t('stockOnHand.stockLevel')}
            multiple
            value={stock}
            onChange={handleStockLevelChange}
            renderValue={(selected) => selected.toString()}
            MenuProps={MenuProps}
          >
            {stockLevelOptions.map((option) => (
              <MenuItem key={option.name}
                value={option.name}>
                <Checkbox checked={stock.some(stockLevel =>
                  stockLevel === option.name)} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}
        md={4}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="category-multiple-checkbox-label">{t('stockOnHand.category')}</InputLabel>
          <Select
            labelId="category-multiple-checkbox-label"
            id="category-multiple-checkbox"
            size='small'
            label={t('stockOnHand.category')}
            multiple
            value={category}
            onChange={handleCategoryChange}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {props.stockOnHands?.categories?.filter((name) => name !== null).map((name) => (
              <MenuItem key={name}
                value={name}>
                <Checkbox checked={category.indexOf(name) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}
        md={4}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="store-multiple-checkbox-label">{t('Store')}</InputLabel>
          <Select
            labelId="store-multiple-checkbox-label"
            id="store-multiple-checkbox"
            size='small'
            label={t('Store')}
            multiple
            value={stores}
            onChange={handleStoreChange}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {user?.stores?.map((store) => (
              <MenuItem key={store.storeNumber}
                value={store.storeName}>
                <Checkbox checked={stores.some(s =>
                  s === store.storeName)} />
                <ListItemText primary={store.storeName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item
        xs={12}
        sm={6}
        md={6}>
        <Button
          fullWidth
          variant="secondary"
          size="lg"
          onClick={clearAll}
        >
          {t('clearAll')}
        </Button>
      </Grid>
      <Grid item
        xs={12}
        sm={6}
        md={6}>
        <Button
          fullWidth
          variant="primary"
          size="lg"
          onClick={searchCall}
          startIcon={<SearchSm />}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <StockOnHandTable isLoading={props.isLoading}
            list={props.stockOnHands}
            isOrderableQuantity={isOrderableQuantity}
            setPageZero={page}
            loadData={loadDataFromOrderTable}
            clearAll={clearAll}
            lastSearch={lastSearch} />
        </Section>
      </Grid>
    </Grid>);
};

StockOnHandBox.displayName = 'StockCountDetails';
export default StockOnHandBox;

